import { Injectable } from '@angular/core';
import { catchError, concatMap, EMPTY, filter, map, Observable, of, switchMap, take, throwError } from 'rxjs';
import { BaseDomainModel } from '../../../models/base/base-domain-model';
import { WebSocketService } from '../../../services/web-socket.service';
import { MediaManagerAssetFolderDto } from '../../../models/image/dto/media-manager-asset-folder-dto';
import { MediaManagerAssetFolderUpdateEventDto } from '../../../models/image/dto/media-manager-asset-folder-update-event-dto';
import { BulkEditAssetsResponse } from '../../../models/image/responses/bulk-edit-assets-response';
import { WebSocketMessageContextCommand } from '../../../models/web-sockets/contexts/web-socket-message-context-command';
import { WebSocketMessageContextDataUpdate } from '../../../models/web-sockets/contexts/web-socket-message-context-data-update';
import { WebSocketMessageContextEvent } from '../../../models/web-sockets/contexts/web-socket-message-context-event';
import { exists } from '../../../functions/exists';
import { MediaManagerLibraryContext, MediaManagerLibraryKey, MediaManagerStore } from '../store';
import {
  applyBulkEditResponse,
  applyCreatedFolder,
  applyDeletedFolder,
  applyUpdatedFolder,
  findAssetsInLibrary,
  findFolderInLibrary,
  findFoldersInLibrary
} from '../mutations';
import {
  findBrandIdForAssetFolderResponse,
  findBrandIdForBulkEditResponse,
  findBrandIdForFolderId,
  getFolderBrandId,
  resolveMediaManagerBrandIdWithActiveFallback
} from '../media-manager-brand-scope';

/**
 * Reconciles remote folder-lifecycle websocket events (folder created, folder updated / soft-deleted, bulk
 * edit responses, force-reload commands) into the store's in-memory state. Every reconciliation path
 * falls back to a full asset-library refetch when the incremental patch cannot apply cleanly, so state drift
 * self-heals within a single event.
 *
 * The `ReloadAssetLibrary` command is intentionally handled here rather than on the store because its
 * effect - a full refetch + filesystem rebuild - is functionally the same fallback the folder-sync patches
 * take when they cannot reconcile.
 *
 * Bootstrapped eagerly by the logged-in scope so listeners are alive as soon as the scope activates.
 */
@Injectable() // Provided by Logged In Scope
export class MediaManagerRemoteFolderSyncService extends BaseDomainModel {
  constructor(
    private webSocketService: WebSocketService,
    private store: MediaManagerStore
  ) {
    super();
    this.setupRemoteAssetFolderCreatedSyncSub();
    this.setupRemoteAssetFolderUpdatedSyncSub();
    this.setupRemoteBulkEditAssetsSyncSub();
    this.setupReloadAssetLibraryCommandSub();
  }

  /** Ms to debounce ReloadAssetLibrary commands so a burst (POS sync) collapses into one refetch. */
  private readonly debounceReloadAssetLibraryCommandsMs = 3000;

  private setupRemoteAssetFolderCreatedSyncSub(): void {
    const remoteAssetFolderCreatedSyncSub = this.webSocketService
      .getRelevantMessageAndThenMapToObjectFromPreSignedUrlInPayload$(
        WebSocketMessageContextEvent.AssetFolderCreated,
        MediaManagerAssetFolderDto
      )
      .pipe(
        filter((createdFolder): createdFolder is MediaManagerAssetFolderDto =>
          exists(createdFolder?.folder?.id?.trim())
        ),
        concatMap(createdFolder => {
          return this.applyRemoteAssetFolderCreated$(createdFolder).pipe(
            catchError(err => {
              console.info(
                `MediaManager could not reconcile created folder from websocket: ${
                  err?.message || err?.error?.message || err
                }`
              );
              return of(null);
            })
          );
        })
      )
      .subscribe();
    this.pushSub(remoteAssetFolderCreatedSyncSub);
  }

  private setupRemoteAssetFolderUpdatedSyncSub(): void {
    const remoteAssetFolderUpdatedSyncSub = this.webSocketService
      .getRelevantMessageAndThenMapToObjectFromPreSignedUrlInPayload$(
        WebSocketMessageContextDataUpdate.AssetFolderUpdated,
        MediaManagerAssetFolderUpdateEventDto
      )
      .pipe(
        filter((event): event is MediaManagerAssetFolderUpdateEventDto => exists(event?.folder?.folder?.id?.trim())),
        concatMap(event => {
          return this.applyRemoteAssetFolderUpdated$(event).pipe(
            catchError(err => {
              console.info(
                `MediaManager could not reconcile updated folder from websocket: ${
                  err?.message || err?.error?.message || err
                }`
              );
              return of(null);
            })
          );
        })
      )
      .subscribe();
    this.pushSub(remoteAssetFolderUpdatedSyncSub);
  }

  private setupRemoteBulkEditAssetsSyncSub(): void {
    const remoteBulkEditAssetsSyncSub = this.webSocketService
      .getRelevantMessageAndThenMapToObjectFromPreSignedUrlInPayload$(
        WebSocketMessageContextDataUpdate.BulkEditAssets,
        BulkEditAssetsResponse
      )
      .pipe(
        concatMap(response => {
          return this.applyRemoteBulkEditAssetsResponse$(response).pipe(
            catchError(err => {
              console.info(
                `MediaManager could not reconcile bulk edited assets from websocket: ${
                  err?.message || err?.error?.message || err
                }`
              );
              return of(null);
            })
          );
        })
      )
      .subscribe();
    this.pushSub(remoteBulkEditAssetsSyncSub);
  }

  /** Debounced so a burst of commands - a POS sync especially - collapses into one refetch and filesystem rebuild. */
  private setupReloadAssetLibraryCommandSub(): void {
    const reloadAssetLibraryCommandSub = this.webSocketService
      .getRelevantMessageDebounced$(
        WebSocketMessageContextCommand.ReloadAssetLibrary,
        msg => `${msg?.entityId}`,
        this.debounceReloadAssetLibraryCommandsMs
      )
      .pipe(
        concatMap(() => {
          this.store.debug.warnFilesystemForceRebuiltFromReloadAssetLibraryCommand();
          const brandId = resolveMediaManagerBrandIdWithActiveFallback(
            null,
            this.store.getActiveBrandIdValue(),
            'reload asset library command'
          );
          if (!brandId) {
            return of(null);
          }
          return this.store.refreshBrandAssetLibrary$(brandId).pipe(
            catchError(err => {
              console.info(
                `MediaManager could not reload asset library from websocket command: ${
                  err?.message || err?.error?.message || err
                }`
              );
              return of(null);
            })
          );
        })
      )
      .subscribe();
    this.pushSub(reloadAssetLibraryCommandSub);
  }

  private applyRemoteAssetFolderCreated$(
    createdFolderResponse: MediaManagerAssetFolderDto
  ): Observable<MediaManagerAssetFolderDto | null> {
    return this.getLibraryContextForAssetFolderResponse$(createdFolderResponse, 'remote asset folder created').pipe(
      switchMap(({ brandId, filesystem, assetLibrary }) => {
        const createdFolder = createdFolderResponse.folder;
        if (findFolderInLibrary(createdFolder.id, assetLibrary)) {
          return of(createdFolderResponse);
        }
        const inMemoryPatchSucceeded = applyCreatedFolder(createdFolder, filesystem, assetLibrary);
        if (!inMemoryPatchSucceeded) {
          return this.refetchAssetLibraryAfterRemoteAssetFolderCreated$(createdFolderResponse, brandId);
        }
        this.store.notifyFilesystemChanged('remote asset folder created');
        return of(createdFolderResponse);
      })
    );
  }

  private refetchAssetLibraryAfterRemoteAssetFolderCreated$(
    createdFolderResponse: MediaManagerAssetFolderDto,
    brandId: string
  ): Observable<MediaManagerAssetFolderDto> {
    return this.store.refreshBrandAssetLibrary$(brandId).pipe(map(() => createdFolderResponse));
  }

  private applyRemoteAssetFolderUpdated$(
    event: MediaManagerAssetFolderUpdateEventDto
  ): Observable<MediaManagerAssetFolderUpdateEventDto> {
    return this.getLibraryContextForAssetFolderUpdateEvent$(event, 'remote asset folder updated').pipe(
      switchMap(context => {
        const { filesystem, assetLibrary } = context;
        return this.store.locallyDeletingFolderIds$.pipe(
          take(1),
          switchMap(locallyDeletingFolderIds => {
            if (event.deletedFolder?.id?.trim()) {
              return this.applyRemoteAssetFolderDeleteWithTarget$(event, context, locallyDeletingFolderIds);
            }
            const updatedFolder = event.folder.folder;
            const currentFolder = findFolderInLibrary(updatedFolder.id, assetLibrary);
            if (!currentFolder) {
              return this.refetchAssetLibraryAfterRemoteAssetFolderUpdated$(event, context.brandId);
            }
            const inMemoryPatchSucceeded = applyUpdatedFolder(currentFolder, updatedFolder, filesystem);
            if (!inMemoryPatchSucceeded) {
              return this.refetchAssetLibraryAfterRemoteAssetFolderUpdated$(event, context.brandId);
            }
            this.store.notifyFilesystemChanged('remote asset folder updated');
            this.publishAssetFolderUpdatedToast();
            return of(event);
          })
        );
      })
    );
  }

  private applyRemoteAssetFolderDeleteWithTarget$(
    event: MediaManagerAssetFolderUpdateEventDto,
    context: MediaManagerLibraryContext,
    locallyDeletingFolderIds: Set<string>
  ): Observable<MediaManagerAssetFolderUpdateEventDto> {
    const { brandId, filesystem, assetLibrary } = context;
    const deletedFolderId = event.deletedFolder.id;
    if (this.isDeletedFolderFromLocalDelete(deletedFolderId, locallyDeletingFolderIds)) {
      this.store.debug.logRemoteFolderDeleteSkippedForLocalDelete(
        this.store.debug.getFolderNameForDebugLog(deletedFolderId, assetLibrary)
      );
      return of(event);
    }
    if (!findFolderInLibrary(deletedFolderId, assetLibrary)) {
      return of(event);
    }
    const targetFolderId = event.folder.folder.id;
    const inMemoryPatchSucceeded = applyDeletedFolder(
      deletedFolderId,
      targetFolderId,
      false,
      event.folder,
      filesystem,
      assetLibrary
    );
    if (!inMemoryPatchSucceeded) {
      return this.refetchAssetLibraryAfterRemoteAssetFolderUpdated$(event, brandId);
    }
    this.store.notifyFilesystemChanged('remote asset folder deleted with target');
    this.publishAssetFolderUpdatedToast();
    return of(event);
  }

  private isDeletedFolderFromLocalDelete(folderId: string, locallyDeletingFolderIds: Set<string>): boolean {
    return locallyDeletingFolderIds.has(folderId);
  }

  private refetchAssetLibraryAfterRemoteAssetFolderUpdated$(
    event: MediaManagerAssetFolderUpdateEventDto,
    brandId: string
  ): Observable<MediaManagerAssetFolderUpdateEventDto> {
    return this.store.refreshBrandAssetLibrary$(brandId).pipe(map(() => event));
  }

  private publishAssetFolderUpdatedToast(): void {
    this.store.publishMediaManagerSuccessMessage('1 folder updated!');
  }

  private applyRemoteBulkEditAssetsResponse$(response: BulkEditAssetsResponse): Observable<BulkEditAssetsResponse> {
    return this.getLibraryContextForBulkEditResponse$(response, 'remote bulk edit assets').pipe(
      switchMap(({ brandId, filesystem, assetLibrary }) => {
        const currentFolders = findFoldersInLibrary(response.updatedAssetFolders ?? [], assetLibrary);
        if (!currentFolders) {
          return this.refetchAssetLibraryAfterRemoteBulkEditAssets$(response, brandId);
        }
        const currentAssets = findAssetsInLibrary(response.updatedAssets ?? [], assetLibrary);
        if (!currentAssets) {
          return this.refetchAssetLibraryAfterRemoteBulkEditAssets$(response, brandId);
        }
        const inMemoryPatchSucceeded = applyBulkEditResponse(
          response,
          currentFolders,
          currentAssets,
          filesystem,
          assetLibrary
        );
        if (!inMemoryPatchSucceeded) {
          return this.refetchAssetLibraryAfterRemoteBulkEditAssets$(response, brandId);
        }
        this.store.notifyFilesystemChanged('remote bulk edit assets');
        return of(response);
      })
    );
  }

  private refetchAssetLibraryAfterRemoteBulkEditAssets$(
    response: BulkEditAssetsResponse,
    brandId: string
  ): Observable<BulkEditAssetsResponse> {
    return this.store.refreshBrandAssetLibrary$(brandId).pipe(map(() => response));
  }

  private getLibraryContextForAssetFolderResponse$(
    response: MediaManagerAssetFolderDto,
    reason: string
  ): Observable<MediaManagerLibraryContext> {
    return this.store.assetLibrariesByBrandId$.pipe(
      take(1),
      switchMap(assetLibrariesByBrandId => {
        const resolvedBrandId = findBrandIdForAssetFolderResponse(response, assetLibrariesByBrandId);
        if (!resolvedBrandId || !assetLibrariesByBrandId.has(resolvedBrandId)) {
          return EMPTY;
        }
        return this.getLibraryContextForResolvedBrand$(resolvedBrandId, reason);
      })
    );
  }

  private getLibraryContextForAssetFolderUpdateEvent$(
    event: MediaManagerAssetFolderUpdateEventDto,
    reason: string
  ): Observable<MediaManagerLibraryContext> {
    return this.store.assetLibrariesByBrandId$.pipe(
      take(1),
      switchMap(assetLibrariesByBrandId => {
        const updatedFolder = event?.folder?.folder;
        const resolvedBrandId = resolveMediaManagerBrandIdWithActiveFallback(
          getFolderBrandId(updatedFolder) ||
            getFolderBrandId(event?.deletedFolder) ||
            findBrandIdForFolderId(updatedFolder?.id, assetLibrariesByBrandId) ||
            findBrandIdForFolderId(event?.deletedFolder?.id, assetLibrariesByBrandId) ||
            findBrandIdForFolderId(updatedFolder?.parentFolderId, assetLibrariesByBrandId),
          null,
          reason
        );
        if (!resolvedBrandId || !assetLibrariesByBrandId.has(resolvedBrandId)) {
          return EMPTY;
        }
        return this.store.getBrandLibraryContext$(resolvedBrandId);
      })
    );
  }

  private getLibraryContextForBulkEditResponse$(
    response: BulkEditAssetsResponse,
    reason: string
  ): Observable<MediaManagerLibraryContext> {
    return this.store.assetLibrariesByBrandId$.pipe(
      take(1),
      switchMap(assetLibrariesByBrandId => {
        const resolvedBrandId = findBrandIdForBulkEditResponse(response, assetLibrariesByBrandId);
        if (!resolvedBrandId || !assetLibrariesByBrandId.has(resolvedBrandId)) {
          return EMPTY;
        }
        return this.getLibraryContextForResolvedBrand$(resolvedBrandId, reason);
      })
    );
  }

  private getLibraryContextForResolvedBrand$(
    resolvedBrandId: MediaManagerLibraryKey | null,
    reason: string
  ): Observable<MediaManagerLibraryContext> {
    const brandId = resolveMediaManagerBrandIdWithActiveFallback(resolvedBrandId, null, reason);
    if (!brandId) {
      return throwError(() => new Error(`MediaManager could not resolve brand for '${reason}'.`));
    }
    return this.store.getBrandLibraryContext$(brandId);
  }
}
